// frontend/src/pages/TvShows.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Tv, Loader2, Star, Calendar, MonitorPlay } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TvShows() {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [region, setRegion] = useState('');

  useEffect(() => {
    fetchShows(1);
  }, [region]);

  const fetchShows = async (pageNum) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      // Connects to your existing Python discovery endpoint
      const regionParams = region ? `&region=${region}&with_origin_country=${region}` : '';
      const res = await axios.get(
        `${BACKEND_URL}/api/discover?media_type=tv&sort_by=popularity.desc&page=${pageNum}${regionParams}`
      );
      setShows(prev => pageNum === 1 ? res.data.results : [...prev, ...res.data.results]);
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to fetch TV shows:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Page Header */}
        <div className="mb-10 text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/15 border border-primary/20 mb-5">
            <Tv className="w-7 h-7 text-primary" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">TV Shows</h1>
          <p className="text-muted-foreground text-lg">
            Explore trending series, critically acclaimed masterpieces, and find your next binge-watch.
          </p>
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setRegion(prev => (prev === 'IN' ? '' : 'IN'))}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors border ${
                region === 'IN'
                  ? 'bg-primary/20 text-primary border-primary/40'
                  : 'bg-white/5 text-gray-200 border-white/15 hover:bg-white/10 hover:text-white'
              }`}
            >
              🇮🇳 Indian Content
            </button>
          </div>
        </div>

        {/* Dynamic Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6 min-h-[500px]">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="aspect-[2/3] rounded-2xl skeleton-shimmer bg-muted/20" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
              {shows.map((show, idx) => (
                <Link
                  key={`${show.id}-${idx}`}
                  to={`/tv/${show.id}`}
                  className="group relative rounded-2xl overflow-hidden bg-card border border-white/5 hover:border-primary/50 transition-all duration-300 block"
                >
                  <div className="aspect-[2/3] relative overflow-hidden">
                    {show.poster_path ? (
                      <img
                        src={`https://image.tmdb.org/t/p/w500${show.poster_path}`}
                        alt={show.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted/20 flex items-center justify-center">
                        <MonitorPlay className="w-10 h-10 text-muted-foreground" />
                      </div>
                    )}
                    
                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />

                    {/* Rating Badge */}
                    <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-md px-2 py-1 rounded-md border border-white/10 flex items-center gap-1.5">
                      <Star className="w-3.5 h-3.5 text-yellow-400" fill="currentColor" />
                      <span className="text-xs font-bold text-white">
                        {show.vote_average ? show.vote_average.toFixed(1) : 'NR'}
                      </span>
                    </div>

                    {/* Content Info */}
                    <div className="absolute bottom-0 left-0 w-full p-4 transform translate-y-2 group-hover:translate-y-0 transition-transform">
                      <h3 className="font-bold text-white leading-tight mb-1 line-clamp-2">
                        {show.name}
                      </h3>
                      <p className="text-xs text-gray-400 flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" />
                        {show.first_air_date ? show.first_air_date.substring(0, 4) : 'TBA'}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Load More Button */}
            {shows.length > 0 && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={() => fetchShows(page + 1)}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-8 py-3 bg-white/5 border border-white/10 rounded-full text-sm font-bold text-white hover:bg-white/10 transition-all active:scale-95 disabled:opacity-50"
                >
                  {loadingMore ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Loading...</>
                  ) : (
                    'Load More Shows'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
